import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import PauseIcon from "@mui/icons-material/PauseRounded";
import ArchiveIcon from "@mui/icons-material/Inventory2Outlined";
import PlayIcon from "@mui/icons-material/PlayArrowRounded";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import AddIcon from "@mui/icons-material/AddOutlined";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { DataTable, type Column } from "@/components/data/data-table";
import { FilterSelect } from "@/components/data/filter-select";
import { StatusChip } from "@/components/display/status-chip";
import { RelativeTime } from "@/components/display/relative-time";
import { MonoText } from "@/components/display/mono";
import { EmptyState } from "@/components/feedback/states";
import { useUndoableDelete, usePendingDeletes } from "@/lib/undoable-delete";
import { ScheduleForm } from "@/components/schedules/schedule-form";
import { humanizeCron } from "@/lib/cron";
import { type Schedule, useScheduleMutations, useSchedules } from "@/api/schedules";
import { usePipelineNames, usePipelines } from "@/api/pipelines";

export const Route = createFileRoute("/_app/schedules/")({
  component: SchedulesList,
});

function SchedulesList() {
  const [showArchived, setShowArchived] = useState(false);
  const { data, isLoading, error, refetch } = useSchedules(showArchived);
  const names = usePipelineNames();
  const pipelines = usePipelines();
  const m = useScheduleMutations();
  const pendingDeletes = usePendingDeletes();
  const del = useUndoableDelete<{ id: string; name: string }>({
    noun: "Schedule",
    key: (i) => i.id,
    name: (i) => i.name,
    mutate: (i) => m.remove.mutateAsync(i.id),
    invalidate: [["schedules"]],
  });
  const [form, setForm] = useState<{ open: boolean; schedule: Schedule | null }>({
    open: false,
    schedule: null,
  });
  const [trigger, setTrigger] = useState("");
  const [status, setStatus] = useState("");

  const pipelineOptions = useMemo(
    () => (pipelines.data?.content ?? []).map((p) => ({ id: p.id, name: p.name })),
    [pipelines.data],
  );

  const rows = useMemo(() => {
    let list = (data ?? []).filter((r) => !pendingDeletes.has(r.id));
    if (trigger) list = list.filter((r) => (r.triggerType ?? "CRON") === trigger);
    if (status) list = list.filter((r) => (r.status ?? "ACTIVE") === status);
    return list;
  }, [data, trigger, status, pendingDeletes]);

  const columns: Column<Schedule>[] = [
    { id: "name", header: "Name", cell: (r) => r.name, sortValue: (r) => r.name },
    { id: "trigger", header: "Trigger", cell: (r) => r.triggerType ?? "CRON", nowrap: true },
    {
      id: "when",
      header: "When",
      cell: (r) => (
        <Stack spacing={0.25}>
          {r.cronExpr ? <MonoText>{r.cronExpr}</MonoText> : null}
          <span style={{ opacity: 0.7, fontSize: "0.8em" }}>
            {r.triggerType === "FOLDER_WATCH" && r.watchPath
              ? r.watchPath
              : humanizeCron(r.cronExpr)}
          </span>
        </Stack>
      ),
    },
    {
      id: "pipeline",
      header: "Pipelines",
      cell: (r) =>
        r.bindings && r.bindings.length > 0
          ? r.bindings.length === 1
            ? (names[String(r.bindings[0]!.pipelineId)] ?? "1 pipeline")
            : `${r.bindings.length} pipelines`
          : r.pipelineId
            ? (names[r.pipelineId] ?? r.pipelineId)
            : "—",
    },
    { id: "status", header: "Status", cell: (r) => <StatusChip status={r.status ?? "ACTIVE"} /> },
    { id: "next", header: "Next run", cell: (r) => <RelativeTime value={r.nextRunAt} /> },
    {
      id: "actions",
      header: "",
      align: "right",
      cell: (r) => {
        const paused = r.status === "PAUSED";
        return (
          <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
            <IconButton
              size="small"
              aria-label={paused ? "Resume schedule" : "Pause schedule"}
              onClick={(e) => {
                e.stopPropagation();
                m.setPaused.mutate({ id: r.id, paused: !paused });
              }}
            >
              {paused ? <PlayIcon fontSize="inherit" /> : <PauseIcon fontSize="inherit" />}
            </IconButton>
            <IconButton
              size="small"
              aria-label={r.archivedAt ? "Unarchive schedule" : "Archive schedule"}
              onClick={(e) => {
                e.stopPropagation();
                m.setArchived.mutate({ id: r.id, archived: !r.archivedAt });
              }}
            >
              <ArchiveIcon fontSize="inherit" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              aria-label="Delete schedule"
              onClick={(e) => {
                e.stopPropagation();
                del.remove({ id: r.id, name: r.name });
              }}
            >
              <DeleteIcon fontSize="inherit" />
            </IconButton>
          </Stack>
        );
      },
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Schedules"
        description="Run pipelines on a cron expression or when files land in a folder."
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setForm({ open: true, schedule: null })}
          >
            New schedule
          </Button>
        }
      />
      <Card>
        <DataTable
          columns={columns}
          data={isLoading || error ? undefined : rows}
          isLoading={isLoading}
          error={error}
          onRetry={() => refetch()}
          rowKey={(r) => r.id}
          search={(r) => `${r.name} ${r.cronExpr ?? ""} ${r.watchPath ?? ""}`}
          searchPlaceholder="Search schedules…"
          filters={
            <>
              <FilterSelect
                label="Trigger"
                value={trigger}
                onChange={setTrigger}
                options={[
                  { value: "CRON", label: "Cron" },
                  { value: "FOLDER_WATCH", label: "Folder watch" },
                ]}
              />
              <FilterSelect
                label="Status"
                value={status}
                onChange={setStatus}
                options={["ACTIVE", "PAUSED", "ERROR", "COMPLETED"]}
              />
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                  />
                }
                label="Show archived"
              />
            </>
          }
          onRowClick={(r) => setForm({ open: true, schedule: r })}
          empty={
            <EmptyState
              title="No schedules yet"
              description="Add a cron or folder-watch trigger to run pipelines automatically."
            />
          }
        />
      </Card>

      <ScheduleForm
        open={form.open}
        onClose={() => setForm({ open: false, schedule: null })}
        schedule={form.schedule}
        pipelines={pipelineOptions}
      />
    </PageContainer>
  );
}
